/**
 * @module virtual-collection
 * Entry point — defines the <virtual-collection-form> custom element that adds
 * multiple product variants to the cart in a single action. Also handles
 * individual "Add to Cart" links. Quantity stepper is managed by Dawn's
 * native <quantity-input> element; this module reads its value on submit.
 */

import { SELECTORS } from './constants.js';
import { addWithFeedback } from './cart.js';
import type { CartElement, Variant } from './cart.js';
import { getQuantity } from './ui.js';
// Ships in this bundle, which theme.liquid loads on every page, so the cart helpers load once.
import '../byo-bundle/index.js';

export { SELECTORS, CLASSES, SOURCE } from './constants.js';
export { addItemsToCart, addWithFeedback, handleCartSuccess, publishCartEvent } from './cart.js';
export { setLoading, clearLoading, handleErrorMessage } from './ui.js';

if (!customElements.get('virtual-collection-form')) {
  customElements.define(
    'virtual-collection-form',
    class VirtualCollectionForm extends HTMLElement {
      declare variants: Variant[];
      declare _onSubmit: EventListener;
      declare _addSingleLinks: Array<{ element: HTMLButtonElement; handler: EventListener }>;
      declare submitButton: HTMLElement;
      declare root: ParentNode;
      declare stepperInput: HTMLInputElement | null;
      declare cart: CartElement | null;

      constructor() {
        super();
        this.variants = [];
        this._onSubmit = this.onSubmitHandler.bind(this);
        this._addSingleLinks = [];
      }

      connectedCallback() {
        this.submitButton = this.querySelector<HTMLElement>(SELECTORS.submitButton)!;
        // The section holds the product rows' add buttons and error alerts.
        this.root = this.closest('section') || this.closest('[data-section]') || this;
        this.stepperInput = this.querySelector<HTMLInputElement>(SELECTORS.stepperValue);
        this.cart =
          (document.querySelector('cart-notification') as (Element & CartElement) | null) ||
          (document.querySelector('cart-drawer') as (Element & CartElement) | null);

        const dataScript = this.querySelector<HTMLScriptElement>(SELECTORS.variantData);
        try {
          this.variants = dataScript
            ? (JSON.parse(dataScript.textContent!) as unknown as Variant[])
            : [];
        } catch {
          this.variants = [];
        }

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this._onSubmit);

          if (document.querySelector('cart-drawer')) {
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
          }
        }

        this.bindAddSingleLinks();
      }

      disconnectedCallback() {
        if (this.submitButton) {
          this.submitButton.removeEventListener('click', this._onSubmit);
        }

        this._addSingleLinks.forEach(({ element, handler }) => {
          element.removeEventListener('click', handler);
        });
        this._addSingleLinks = [];
      }

      bindAddSingleLinks() {
        this.root.querySelectorAll<HTMLButtonElement>(SELECTORS.addSingleLink).forEach((link) => {
          const handler = (evt: Event) => this.onAddSingleHandler(evt);
          link.addEventListener('click', handler);
          this._addSingleLinks.push({ element: link, handler });
        });
      }

      async onSubmitHandler(evt: Event) {
        evt.preventDefault();
        await addWithFeedback({
          button: this.submitButton,
          root: this.root,
          errorScope: this,
          cart: this.cart,
          variants: this.variants,
          quantity: getQuantity(this.stepperInput),
        });
      }

      async onAddSingleHandler(evt: Event) {
        evt.preventDefault();
        const button = evt.currentTarget as HTMLButtonElement;
        const variantId = parseInt(button.dataset.variantId!, 10);
        if (!variantId) return;

        await addWithFeedback({
          button,
          root: this.root,
          errorScope: button.closest(SELECTORS.productCard),
          cart: this.cart,
          variants: [{ id: variantId, available: true }],
        });
      }
    },
  );
}
